
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { IFilterBar, FilterGroup } from '@shared/models/interfaces';

export interface FilterStateConfig {
  storageNamespace: string; // used to derive storage keys
  defaultState: IFilterBar;
  extraResetKeys?: string[];
}

@Injectable()
export class FilterStateServiceBase {
  private readonly config: FilterStateConfig;

  private filterStateSubject: BehaviorSubject<IFilterBar>;
  filterState$;

  private filterOptionsSubject: BehaviorSubject<FilterGroup[]>;
  filterOptions$;

  constructor(config: FilterStateConfig) {
    this.config = config;
    this.filterStateSubject = new BehaviorSubject<IFilterBar>(this.readFilterState());
    this.filterState$ = this.filterStateSubject.asObservable();

    this.filterOptionsSubject = new BehaviorSubject<FilterGroup[]>(this.readFilterOptions());
    this.filterOptions$ = this.filterOptionsSubject.asObservable();
  }

  setFilterState(state: IFilterBar) {
    const normalized = this.normalizeState(state);
    this.filterStateSubject.next(normalized);
    try {
      sessionStorage.setItem(this.FILTER_STATE_KEY, JSON.stringify(normalized));
    } catch {}
  }

  getFilterState(): IFilterBar {
    return this.filterStateSubject.value;
  }

  setFilterOptions(options: FilterGroup[]) {
    this.filterOptionsSubject.next(options);
    try {
      sessionStorage.setItem(this.FILTER_OPTIONS_KEY, JSON.stringify(options));
    } catch {}
  }

  getFilterOptions(): FilterGroup[] {
    return this.filterOptionsSubject.value;
  }

  resetState() {
    this.filterStateSubject.next(this.config.defaultState);
    this.filterOptionsSubject.next([]);
    sessionStorage.removeItem(this.FILTER_STATE_KEY);
    sessionStorage.removeItem(this.FILTER_OPTIONS_KEY);

    // Clean any extra feature-specific keys
    this.config.extraResetKeys?.forEach((key) => {
      try {
        sessionStorage.removeItem(key);
      } catch {}
    });
  }

  private readFilterState(): IFilterBar {
    const raw = sessionStorage.getItem(this.FILTER_STATE_KEY);
    if (!raw) return this.config.defaultState;
    try {
      const parsed = JSON.parse(raw) as IFilterBar;
      return this.normalizeState(parsed);
    } catch {
      sessionStorage.removeItem(this.FILTER_STATE_KEY);
      return this.config.defaultState;
    }
  }

  private readFilterOptions(): FilterGroup[] {
    const raw = sessionStorage.getItem(this.FILTER_OPTIONS_KEY);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw) as FilterGroup[];
      if (!Array.isArray(parsed)) return [];
      parsed.forEach((g) => {
        g.selected = !!g.selected;
        g.indeterminate = !!g.indeterminate;
        g.options = Array.isArray(g.options)
          ? g.options.map((o) => ({
              name: String(o.name),
              selected: !!o.selected,
                ...(o.code && { code : o.code }) 

            }))
          : [];
      });
      return parsed;
    } catch {
      sessionStorage.removeItem(this.FILTER_OPTIONS_KEY);
      return [];
    }
  }

  private normalizeState(state: IFilterBar): IFilterBar {
    const cleanCsv = (s: string | undefined | null) =>
      (s ?? '')
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean)
        .join(',');

    return {
      search: state.search ?? '',
      sort: state.sort ?? this.config.defaultState.sort,
      filter: cleanCsv(state.filter),
      searchBy: state.searchBy ?? this.config.defaultState.searchBy,
      direction: state.direction ?? this.config.defaultState.direction,
    };
  }

  // derived storage keys
  private get FILTER_STATE_KEY() {
    return `${this.config.storageNamespace}FilterState`;
  }
  private get FILTER_OPTIONS_KEY() {
    return `${this.config.storageNamespace}FilterOptions`;
  }
}
